import React, { useState, useEffect } from "react"
import { Box, Button, Typography } from "@mui/material"
import { Link, useLocation } from "react-router-dom"
import "@/components/calendar/calendar.css"
import { Event, RawServerEvent } from "@/common/types"
import { remapRawServerEvents } from "@/common/utils"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import EventDetails from "../eventDetails/EventDetails"
import { useEventContext } from "@/contexts/EventContext"
import { useQueryClient } from "@tanstack/react-query"
import useGetCalendarEvents from "@/hooks/api/events/useGetCalendarEvents"
import queryKeys from "@/api/queryKeys"
import dayjs from "dayjs"
import useIsMobile from "@/hooks/useIsMobile"
import ReactGA from "react-ga4"
import { useGetMe } from "@/hooks/useGetMe"
interface CalendarProps {
  selectedDate: Date
  onDateSelect?: (date: Date) => void
}

function getCalendarLink(date: Date): string {
  return `/calendar/${date.getFullYear()}/${date.getMonth() + 1}`
}

/**
 * This creates an array with as many items as there are days in
 * a month to enable quick lookup when building the calendar days
 */
function getCalendarEventArray(date: Date): (Event[] | null)[] {
  const curMonthDayCountDate = new Date(date)
  curMonthDayCountDate.setMonth(curMonthDayCountDate.getMonth() + 1)
  curMonthDayCountDate.setDate(0)
  const curMonthDayCount = curMonthDayCountDate.getDate()

  return new Array(curMonthDayCount).fill(null)
}

const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}

const Calendar: React.FC<CalendarProps> = ({ selectedDate }) => {
  const { data: me } = useGetMe()
  const isMobile = useIsMobile()
  const location = useLocation()
  const prevMonthDate = new Date(selectedDate)
  const nextMonthDate = new Date(selectedDate)
  prevMonthDate.setMonth(prevMonthDate.getMonth() - 1)
  nextMonthDate.setMonth(nextMonthDate.getMonth() + 1)
  const [calendarSelectedDate, setCalendarSelectedDate] = useState<Date | null>(
    () => {
      const stored = sessionStorage.getItem("calendarSelectedDate")
      return stored ? new Date(Number(stored)): null
    }
  )

  //persists the selected day across remounts
  const updateSelectedDate = (date: Date | null) => {
    setCalendarSelectedDate(date)
    if (date) {
      sessionStorage.setItem("calendarSelectedDate", String(date.getTime()))
    } else {
      sessionStorage.removeItem("calendarSelectedDate")
    }
  }
  const [events, setEvents] = useState(getCalendarEventArray(selectedDate))
  const queryClient = useQueryClient()
  const { searchVal, value, ageRangeFilter, petFriendlyFilter, priceFilter } =
    useEventContext()

  const { data: eventResponse, isSuccess } = useGetCalendarEvents({
    filters: {
      type: "calendar",
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth(),
      searchValue: searchVal,
      filterValue: value,
      ageRange: ageRangeFilter,
      petFriendly: petFriendlyFilter,
      price: priceFilter,
    },
  })
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.events.calendarPage({
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth(),
        searchValue: searchVal,
        filterValue: value,
        ageRange: ageRangeFilter,
        petFriendly: petFriendlyFilter,
        price: priceFilter,
      }).queryKey,
    })

    if (isSuccess && eventResponse) {
      const newCalendarEvents = getCalendarEventArray(selectedDate)
      for (const event of remapRawServerEvents(
        eventResponse.events as RawServerEvent[]
      )) {
        // For multi-day events, add them to all days they span
        const startDate = event.event_start_timestamp
        const endDate = event.event_end_timestamp || startDate

        // Iterate through each day the event spans
        const currentDate = new Date(startDate)
        while (currentDate <= endDate) {
          // Only include days that fall within the current calendar month
          if (
            currentDate.getMonth() === selectedDate.getMonth() &&
            currentDate.getFullYear() === selectedDate.getFullYear()
          ) {
            const dayIdx = currentDate.getDate() - 1
            const dayEvents =
              newCalendarEvents[dayIdx] ?? (newCalendarEvents[dayIdx] = [])
            dayEvents.push(event)
          }
          // Move to the next day
          currentDate.setDate(currentDate.getDate() + 1)
        }
      }
      setEvents(newCalendarEvents)
    }
  }, [
    eventResponse,
    isSuccess,
    searchVal,
    petFriendlyFilter,
    ageRangeFilter,
    priceFilter,
    selectedDate,
    queryClient,
    value,
  ])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    const currDate = searchParams.get("date")

    if (currDate) {
      setCalendarSelectedDate(new Date(Number(currDate)))
    }
  }, [location.search])

  const buttonStyling = {
    marginLeft: ".5rem",
    backgroundColor: "transparent",
    border: "1px solid #dddddd",
  }
  const renderHeader = () => {
    return (
      <div
        style={{ display: "flex", alignItems: "center", paddingTop: "12px" }}
      >
        <Typography
          variant={isMobile ? "body1" : "h2"}
          color="white"
          sx={{ width: "100%" }}
        >
          {selectedDate.toLocaleString("default", {
            month: "long",
          })}
          , {selectedDate.getFullYear()}
        </Typography>
        <div
          style={{
            display: "flex",
            width: "150px",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={buttonStyling}
            component={Link}
            replace={true}
            to={getCalendarLink(prevMonthDate)}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="contained"
            sx={buttonStyling}
            component={Link}
            replace={true}
            to={getCalendarLink(nextMonthDate)}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
    )
  }

  const renderDaysOfWeek = () => {
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    return (
      <Box
        sx={{
          display: "flex",
          textTransform: "uppercase",
          fontWeight: "bold",
          padding: ".5rem",
          maxWidth: "100%",
        }}
      >
        {daysOfWeek.map((day) => (
          <Box key={day} className="day">
            <Typography color="white" fontSize={isMobile ? 14 : 20}>
              {day}
            </Typography>
          </Box>
        ))}
      </Box>
    )
  }

  const renderCells = () => {
    const firstDayOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1
    )
    const lastDayOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0
    )
    const daysInMonth = lastDayOfMonth.getDate()
    const firstDayOfWeek = firstDayOfMonth.getDay()
    const weeks: JSX.Element[] = []

    const today = new Date()
    let currentDay = 1

    for (let i = 0; i < 6; i++) {
      const days: JSX.Element[] = []
      let isEventWeek = false
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < firstDayOfWeek) {
          days.push(<div key={`${i}-${j}`} className="empty-cell"></div>)
        } else if (currentDay <= daysInMonth) {
          const date = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            currentDay
          )
          const isSelected =
            calendarSelectedDate && isSameDay(date, calendarSelectedDate)
          const isToday = isSameDay(date, today)
          const cellEvents = events[currentDay - 1]
          if (isSelected) isEventWeek = true
          days.push(
            <Box
              key={currentDay}
              className={`cell ${isSelected ? "selected" : ""} ${
                isToday ? "today" : ""
              }`}
              onClick={() => {
                ReactGA.event({
                  category: "Calendar",
                  action: "Click day",
                  label: `${date.toDateString()} | | User: ${
                    me?.id ?? "guest"
                  }`,
                  value: date.getDate(),
                })
                if (dayjs(date).isSame(calendarSelectedDate)) {
                  updateSelectedDate(null)
                } else {
                  updateSelectedDate(date)
                }
              }}
            >
              {currentDay}
              {cellEvents && cellEvents?.length > 0 && (
                <Box
                  className={`cell-events ${
                    isSelected && isMobile ? "event-selected" : ""
                  }`}
                >
                  <Typography fontSize={14}>
                    <strong>{cellEvents.length}</strong>
                  </Typography>
                  {cellEvents.length === 1 ? "event" : "events"}
                </Box>
              )}
            </Box>
          )
          currentDay++
        } else {
          days.push(<div key={`${i}-${j}`} className="empty-cell"></div>)
        }
      }

      weeks.push(
        <Box key={i} className="week">
          {days}
        </Box>
      )

      if (isEventWeek && calendarSelectedDate) {
        const selectedEvents = events[calendarSelectedDate.getDate() - 1] ?? []

        weeks.push(
          <Box
            key={`event-${i}`}
            sx={{
              marginTop: "1rem",
              display: "flex",
            }}
          >
            <EventDetails
              events={selectedEvents}
              selectedDate={calendarSelectedDate.getDate()}
              selectedCalendarDate={calendarSelectedDate}
            />
          </Box>
        )
      }

      if (currentDay > daysInMonth) break
    }

    return weeks
  }

  return (
    <Box
      // className="calendar"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: { md: "70%", lg: "70%", sm: "86%", xs: "96%" },
        minHeight: "100vh",
        borderRadius: "5px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {renderHeader()}
      {renderDaysOfWeek()}
      {renderCells()}
    </Box>
  )
}

export default Calendar
